import Image from 'next/image';
import type {RefObject} from 'react';
import {m as motion} from 'motion/react';
import {useResizeObserver} from 'usehooks-ts';
import getImageUrl from 'src/utils/getImageUrl';

const AnimatedImage = motion.create(Image);

const getImageSize = (image: PlaceImage | undefined, containerHeight: number, containerWidth: number) => {
  if (!image) return {width: '100%', height: '100%'};

  const imageRatio = image.width / image.height;
  const containerRatio = containerWidth / containerHeight;

  if (imageRatio > containerRatio) {
    return {
      width: containerWidth,
      height: containerWidth / imageRatio
    };
  }

  return {
    width: containerHeight * imageRatio,
    height: containerHeight
  };
};

let lastSize: {height: string | number; width: string | number} = {height: '100%', width: '100%'};
let initialSize: {height: string | number; width: string | number} | undefined = undefined;

type Props = {
  id: string;
  respectAspectRatio: boolean;
  image: PlaceImage | undefined;
  ref?: RefObject<HTMLDivElement>;
  isFirstQuestion: boolean;
};

const layoutTransition = {duration: 0.7, ease: [0.2, 0.92, 0.34, 1]};

const QuizQuestionImage = ({id, respectAspectRatio, image, ref, isFirstQuestion}: Props) => {
  const {height, width} = useResizeObserver({ref: ref as RefObject<HTMLDivElement>});

  if (height && width) lastSize = {height, width};

  const isMeasured = height && width;
  const imageSize = respectAspectRatio && height && width ? getImageSize(image, height, width) : lastSize;

  if (isMeasured && !initialSize) initialSize = imageSize;

  return (
    <motion.div
      className="relative grow w-full flex justify-center items-center px-(--side-padding)"
      ref={ref}
      key={id}
      initial={{opacity: 0, scale: 0.6, y: isFirstQuestion ? 0 : 200}}
      animate={{
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {duration: 0.7, delay: 0.1, ease: [0.83, 0, 0.17, 1]}
      }}
      exit={{
        opacity: 0,
        scale: 0.8,
        y: -10,
        transition: {duration: 0.8, ease: [0.2, 0.92, 0.34, 1]}
      }}
      transition={layoutTransition}
    >
      {isMeasured ? (
        <motion.div
          style={{borderRadius: 12, ...initialSize}}
          layout
          transition={layoutTransition}
          animate={{...imageSize}}
          className="nice-image absolute flex grow justify-center rounded-xl items-center after:rounded-xl [box-shadow:var(--quiz-shadow)] dark:[box-shadow:none] transition-shadow origin-center"
        >
          <motion.div
            style={{aspectRatio: (image?.width || 1) / (image?.height || 1)}}
            initial={{scale: 1.3}}
            animate={{scale: 1, transition: {duration: 20}}}
            exit={{scale: 1}}
            layout
            transition={layoutTransition}
            className="relative min-w-full min-h-full"
          >
            <AnimatedImage
              src={getImageUrl(image?.path)}
              alt="Quiz question image"
              className="object-cover"
              fill
              priority
            />
          </motion.div>
        </motion.div>
      ) : null}
    </motion.div>
  );
};

export default QuizQuestionImage;
